import { useState, type FormEvent } from 'react';
import { Modal } from './Modal';
import type { Card } from '../../types/board';

const SKIP_MERGE_CONFIRMATION_KEY = 'retrojet-skip-merge-confirmation';

interface MergeCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (skipFutureConfirmation: boolean) => void;
  sourceCard: Card | null;
  targetCard: Card | null;
  isLoading?: boolean;
}

export function MergeCardModal({
  isOpen,
  onClose,
  onConfirm,
  sourceCard,
  targetCard,
  isLoading = false,
}: MergeCardModalProps): JSX.Element {
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!isLoading) {
      if (dontAskAgain) {
        localStorage.setItem(SKIP_MERGE_CONFIRMATION_KEY, 'true');
      }
      onConfirm(dontAskAgain);
    }
  };

  if (!sourceCard || !targetCard) return <></>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Merge Cards">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-gray-600 dark:text-gray-400">
          Are you sure you want to merge these cards? This action cannot be undone.
        </p>

        <div className="space-y-3">
          <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Source card (will be merged into target):
            </p>
            <p className="text-sm text-gray-900 dark:text-white">{sourceCard.content}</p>
          </div>
          <div className="flex justify-center">
            <svg
              className="w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </div>
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-xs text-blue-500 dark:text-blue-400 mb-1">
              Target card (will contain merged content):
            </p>
            <p className="text-sm text-gray-900 dark:text-white">{targetCard.content}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="dontAskAgain"
            checked={dontAskAgain}
            onChange={(e) => setDontAskAgain(e.target.checked)}
            disabled={isLoading}
            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <label
            htmlFor="dontAskAgain"
            className="text-sm text-gray-600 dark:text-gray-400"
          >
            Don't ask me again
          </label>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Merging...' : 'Merge Cards'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function shouldSkipMergeConfirmation(): boolean {
  return localStorage.getItem(SKIP_MERGE_CONFIRMATION_KEY) === 'true';
}
